/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {useProductList} from '../util/product-list-context'

import {
    SimpleGrid,
    Button,
    Box,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {
    ADD_FILTER,
    REMOVE_FILTER
} from '@salesforce/retail-react-app/app/pages/product-list/partials/refinements-utils'
import {REFINEMENT_LEGEND} from '../../product-list'

/**
 * SizeRefinements is a React functional component that renders a grid of size options.
 *
 * This component is memoized using React.memo, meaning it will only re-render when its props change.
 * The props are used solely for data representation, as the component's actions are derived from the
 * useProductList hook, which provides the toggleFilter function to handle filter interactions.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.filter - The filter object containing size options and their metadata.
 * @param {Array} props.selectedFilters - An array of currently selected filter values.
 *
 * @returns {JSX.Element} A grid of buttons representing size options
 */
const SizeRefinements = ({filter, selectedFilters}) => {
    const {formatMessage} = useIntl()
    const {toggleFilter} = useProductList()

    const styles = useMultiStyleConfig('SwatchGroup', {
        variant: 'sizeRefinement',
        disabled: false
    })

    return (
        filter.values?.length > 0 && (
            <Box as="fieldset">
                {/* ADA legend */}
                <legend className="sr-only">
                    {formatMessage(REFINEMENT_LEGEND, {label: filter.label})}
                </legend>

                <SimpleGrid
                    as="ul"
                    listStyleType={'none'}
                    width="full"
                    templateColumns="repeat(5, 1fr)"
                    spacingX={0}
                    spacingY={10}
                >
                    {filter.values?.map((value, idx) => {
                        // Note the loose comparison, for "string == number" checks.
                        const isSelected = selectedFilters.some(
                            (filterValue) => filterValue == value.value
                        )
                        // Don't display refinements with no results, unless we got there by selecting too
                        // many refinements
                        if (value.hitCount === 0 && !isSelected) return

                        return (
                            <Box as="li" key={`size-button-${value.value}`}>
                                <Button
                                    {...styles.swatch}
                                    role="checkbox"
                                    variant="outline"
                                    borderColor={isSelected ? 'fullBlack' : 'gray'}
                                    backgroundColor={isSelected ? 'fullBlack' : 'transparent'}
                                    color={isSelected ? 'white' : 'darkGray'}
                                    zIndex={isSelected ? 1 : 0}
                                    onClick={() =>
                                        toggleFilter(value, filter.attributeId, isSelected)
                                    }
                                    aria-checked={isSelected}
                                    aria-label={formatMessage(
                                        isSelected ? REMOVE_FILTER : ADD_FILTER,
                                        value
                                    )}
                                >
                                    {value.label}
                                </Button>
                            </Box>
                        )
                    })}
                </SimpleGrid>
            </Box>
        )
    )
}

SizeRefinements.propTypes = {
    filter: PropTypes.object,
    selectedFilters: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string])
}

// Memoized SizeRefinements to prevent unnecessary re-renders.
// Use case: accordion open / close with SizeRefinements
export default React.memo(SizeRefinements)
